import React, { useState, useEffect } from 'react';
import './index.css';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Alert, AlertDescription } from './components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './components/ui/dropdown-menu';
import { Progress } from './components/ui/progress';
import { Skeleton } from './components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Switch } from './components/ui/switch';
import { Separator } from './components/ui/separator';
import Statistics from './components/Statistics';
import Charts from './components/Charts';
import { Widget, MetricWidget, ActivityWidget, QuickActionsWidget, SystemStatusWidget } from './components/Widgets';
import DataTable from './components/DataTable';
import TestimonialsDemo from './components/TestimonialsDemo';
import TwoFactorAuth from './components/TwoFactorAuth';
import Layout from './components/Layout';
import {
  BarChart3,
  CreditCard,
  Users,
  Shield,
  Settings,
  HelpCircle,
  Search,
  MoreVertical,
  RefreshCw,
  Maximize2,
  Plus,
  TrendingUp,
  Activity,
  DollarSign,
  UserCheck,
  AlertTriangle,
  Eye,
  Edit,
  Trash2,
  Download,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// API Configuration
const API_BASE_URL = 'https://neobridge-platform.mks-alghafil.workers.dev/api/v1';

interface Payment {
  id: string;
  amount: number;
  currency: string;
  fromCountry: string;
  toCountry: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  timestamp: string;
  recipientName: string;
  recipientAccount: string;
  exchangeRate: number;
  fees: number;
  description?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'operator' | 'viewer';
  status: 'active' | 'inactive' | 'suspended';
  lastLogin: string;
  country: string;
  permissions: string[];
}

interface ComplianceAlert {
  id: string;
  type: 'sanctions' | 'aml' | 'kyc' | 'fraud';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  timestamp: string;
  assignedTo: string;
  status: 'open' | 'investigating' | 'resolved';
  entityId: string;
}

interface CreatePaymentFormProps {
  onSubmit: (data: Partial<Payment>) => Promise<void>;
  loading: boolean;
}

const CreatePaymentForm: React.FC<CreatePaymentFormProps> = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    amount: '',
    currency: 'USD',
    recipient: '',
    description: '',
    fromCountry: 'US',
    toCountry: 'IR'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSubmit({
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        recipientName: formData.recipient,
        description: formData.description,
        fromCountry: formData.fromCountry,
        toCountry: formData.toCountry,
        status: 'pending',
        timestamp: new Date().toISOString()
      });
      // Reset form
      setFormData({
        amount: '',
        currency: 'USD',
        recipient: '',
        description: '',
        fromCountry: 'US',
        toCountry: 'IR'
      });
    } catch (error) {
      console.error('Error creating payment:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="amount" className="text-sm font-medium">Amount</label>
        <input
          id="amount"
          type="number"
          step="0.01"
          required
          value={formData.amount}
          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          className="w-full p-2 border rounded-md"
          placeholder="Enter amount"
        />
      </div>
      
      <div>
        <label htmlFor="currency" className="text-sm font-medium">Currency</label>
        <select
          id="currency"
          value={formData.currency}
          onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
          className="w-full p-2 border rounded-md"
        >
          <option value="USD">USD</option>
          <option value="EUR">EUR</option>
          <option value="IRR">IRR</option>
          <option value="CNY">CNY</option>
        </select>
      </div>
      
      <div>
        <label htmlFor="recipient" className="text-sm font-medium">Recipient</label>
        <input
          id="recipient"
          type="text"
          required
          value={formData.recipient}
          onChange={(e) => setFormData({ ...formData, recipient: e.target.value })}
          className="w-full p-2 border rounded-md"
          placeholder="Recipient name or email"
        />
      </div>
      
      <div>
        <label htmlFor="description" className="text-sm font-medium">Description</label>
        <textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="w-full p-2 border rounded-md"
          placeholder="Payment description"
          rows={3}
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="fromCountry" className="text-sm font-medium">From Country</label>
          <select
            id="fromCountry"
            value={formData.fromCountry}
            onChange={(e) => setFormData({ ...formData, fromCountry: e.target.value })}
            className="w-full p-2 border rounded-md"
          >
            <option value="US">United States</option>
            <option value="DE">Germany</option>
            <option value="CN">China</option>
            <option value="IR">Iran</option>
          </select>
        </div>
        
        <div>
          <label htmlFor="toCountry" className="text-sm font-medium">To Country</label>
          <select
            id="toCountry"
            value={formData.toCountry}
            onChange={(e) => setFormData({ ...formData, toCountry: e.target.value })}
            className="w-full p-2 border rounded-md"
          >
            <option value="US">United States</option>
            <option value="DE">Germany</option>
            <option value="CN">China</option>
            <option value="IR">Iran</option>
          </select>
        </div>
      </div>
      
      <Button type="submit" disabled={loading} className="w-full">
        {loading ? (
          <>
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            Creating...
          </>
        ) : (
          <>
            <Plus className="mr-2 h-4 w-4" />
            Create Payment
          </>
        )}
      </Button>
    </form>
  );
};

interface CreateUserFormProps {
  onSubmit: (data: Partial<User>) => Promise<void>;
  loading: boolean;
}

const CreateUserForm: React.FC<CreateUserFormProps> = ({ onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'viewer' as 'admin' | 'operator' | 'viewer',
    country: 'US'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await onSubmit({
        ...formData,
        status: 'active',
        lastLogin: new Date().toISOString()
      });
      // Reset form
      setFormData({
        name: '',
        email: '',
        role: 'viewer',
        country: 'US'
      });
    } catch (error) {
      console.error('Error creating user:', error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="text-sm font-medium">Full Name</label>
        <input
          id="name"
          type="text"
          required
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          className="w-full p-2 border rounded-md"
          placeholder="Enter full name"
        />
      </div>

      <div>
        <label htmlFor="email" className="text-sm font-medium">Email</label>
        <input
          id="email"
          type="email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          className="w-full p-2 border rounded-md"
          placeholder="Enter email address"
        />
      </div>

      <div>
        <label htmlFor="role" className="text-sm font-medium">Role</label>
        <select
          id="role"
          value={formData.role}
          onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'operator' | 'viewer' })}
          className="w-full p-2 border rounded-md"
        >
          <option value="viewer">Viewer</option>
          <option value="operator">Operator</option>
          <option value="admin">Admin</option>
        </select>
      </div>

      <div>
        <label htmlFor="country" className="text-sm font-medium">Country</label>
        <select
          id="country"
          value={formData.country}
          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
          className="w-full p-2 border rounded-md"
        >
          <option value="US">United States</option>
          <option value="DE">Germany</option>
          <option value="CN">China</option>
          <option value="IR">Iran</option>
        </select>
      </div>

      <Button type="submit" disabled={loading} className="w-full">
        {loading ? (
          <>
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            Creating...
          </>
        ) : (
          <>
            <Plus className="mr-2 h-4 w-4" />
            Create User
          </>
        )}
      </Button>
    </form>
  );
};

const App: React.FC = () => {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [payments, setPayments] = useState<Payment[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [complianceAlerts, setComplianceAlerts] = useState<ComplianceAlert[]>([]);
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [blockchainNetworks, setBlockchainNetworks] = useState<any[]>([]);
  const [blockchainContracts, setBlockchainContracts] = useState<any[]>([]);
  const [blockchainAnalytics, setBlockchainAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' | 'info' } | null>(null);
  const [showCreatePaymentModal, setShowCreatePaymentModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);

  // Load data on component mount
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load payments from real backend
      const paymentsResponse = await fetch(`${API_BASE_URL}/payment/list`);
      
      if (paymentsResponse.ok) {
        const paymentsData = await paymentsResponse.json();
        setPayments(paymentsData.payments || []);
      }

      // Load analytics dashboard data
      const analyticsResponse = await fetch(`${API_BASE_URL}/analytics/dashboard`);
      
      if (analyticsResponse.ok) {
        const analyticsData = await analyticsResponse.json();
        setAnalyticsData(analyticsData);
        console.log('Analytics data:', analyticsData);
      }

      // Load compliance alerts
      const complianceResponse = await fetch(`${API_BASE_URL}/compliance/alerts`);
      
      if (complianceResponse.ok) {
        const complianceData = await complianceResponse.json();
        setComplianceAlerts(complianceData.alerts || []);
      }

      // Load users
      const usersResponse = await fetch(`${API_BASE_URL}/users`);
      
      if (usersResponse.ok) {
        const usersData = await usersResponse.json();
        setUsers(usersData || []);
      }

      // Load blockchain networks
      const networksResponse = await fetch(`${API_BASE_URL}/blockchain/networks`);
      
      if (networksResponse.ok) {
        const networksData = await networksResponse.json();
        setBlockchainNetworks(networksData || []);
      }

      // Load blockchain contracts
      const contractsResponse = await fetch(`${API_BASE_URL}/blockchain/contracts`);
      
      if (contractsResponse.ok) {
        const contractsData = await contractsResponse.json();
        setBlockchainContracts(contractsData || []);
      }

      // Load blockchain analytics
      const blockchainAnalyticsResponse = await fetch(`${API_BASE_URL}/blockchain/analytics`);
      
      if (blockchainAnalyticsResponse.ok) {
        const blockchainAnalyticsData = await blockchainAnalyticsResponse.json();
        setBlockchainAnalytics(blockchainAnalyticsData);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showMessage('Failed to load data from server. Using offline mode.', 'error');
      
      // Fallback to mock data
      setPayments([
          {
            id: '1',
            amount: 5000,
            currency: 'USD',
          fromCountry: 'US',
          toCountry: 'IR',
          status: 'completed',
          timestamp: '2024-01-15T10:30:00Z',
          recipientName: 'Ahmad Rezaei',
          recipientAccount: 'IR123456789',
          exchangeRate: 42000,
            fees: 25
          },
          {
            id: '2',
            amount: 2500,
            currency: 'EUR',
          fromCountry: 'DE',
          toCountry: 'IR',
          status: 'processing',
          timestamp: '2024-01-15T09:15:00Z',
          recipientName: 'Maryam Karimi',
          recipientAccount: 'IR987654321',
          exchangeRate: 45000,
          fees: 20
        }
      ]);
      
      setUsers([
          {
            id: '1',
          name: 'John Doe',
          email: 'john@neobridge.com',
          role: 'admin',
          status: 'active',
          lastLogin: '2024-01-15T10:00:00Z',
          country: 'US',
          permissions: ['read', 'write', 'admin']
        }
      ]);
      
      setComplianceAlerts([
      {
        id: '1',
          type: 'aml',
          severity: 'high',
          description: 'Unusual transaction pattern detected',
          timestamp: '2024-01-15T10:00:00Z',
          assignedTo: 'John Doe',
          status: 'open',
          entityId: 'user_123'
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const createUser = async (userData: Partial<User>) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (response.ok) {
        const newUser = await response.json();
        setUsers(prev => [...prev, { ...userData, id: newUser.id } as User]);
        showMessage('User created successfully!', 'success');
        setShowCreateUserModal(false);
      } else {
        throw new Error('Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      showMessage('Failed to create user', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text: string, type: 'success' | 'error' | 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const createPayment = async (paymentData: Partial<Payment>) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/payment/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (response.ok) {
        const result = await response.json();
        showMessage('Payment created successfully!', 'success');
        // Reload payments to show the new one
        await loadDashboardData();
        return result;
      } else {
        throw new Error('Failed to create payment');
      }
    } catch (error) {
      showMessage('Error creating payment: ' + (error as Error).message, 'error');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <div className="w-2 h-2 bg-green-500 rounded-full" />;
      case 'processing':
        return <div className="w-2 h-2 bg-yellow-500 rounded-full" />;
      case 'pending':
        return <div className="w-2 h-2 bg-blue-500 rounded-full" />;
      case 'failed':
        return <div className="w-2 h-2 bg-red-500 rounded-full" />;
      default:
        return <div className="w-2 h-2 bg-gray-500 rounded-full" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      completed: 'default',
      processing: 'secondary',
      pending: 'outline',
      failed: 'destructive',
      active: 'default',
      inactive: 'secondary',
      suspended: 'destructive',
      open: 'destructive',
      investigating: 'secondary',
      resolved: 'default'
    } as const;

    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-colors ${
        variants[status as keyof typeof variants] === 'default' ? 'bg-primary text-primary-foreground' :
        variants[status as keyof typeof variants] === 'secondary' ? 'bg-secondary text-secondary-foreground' :
        variants[status as keyof typeof variants] === 'destructive' ? 'bg-destructive text-destructive-foreground' :
        'border border-input bg-background text-foreground'
      }`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <BarChart3 className="h-6 w-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold">Dashboard</h1>
        </div>
        <div className="flex items-center space-x-2">
          <Button variant="ghost" size="icon" onClick={loadDashboardData}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon">
            <Maximize2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <Alert>
          <RefreshCw className="h-4 w-4 animate-spin" />
          <AlertDescription>
            Loading dashboard data...
          </AlertDescription>
        </Alert>
      )}

      {/* Enhanced Statistics */}
      <Statistics
        payments={payments}
        users={users}
        complianceAlerts={complianceAlerts}
        analyticsData={analyticsData}
        blockchainNetworks={blockchainNetworks}
        blockchainContracts={blockchainContracts}
      />

      {/* Charts Section */}
      <Charts payments={payments} analyticsData={analyticsData} />

      {/* Charts and Tables */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Recent Payments */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Payments</CardTitle>
            <CardDescription>Latest cross-border payment transactions</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.slice(0, 5).map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell className="font-medium">{payment.recipientName}</TableCell>
                    <TableCell>{payment.amount.toLocaleString()} {payment.currency}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {payment.fromCountry} → {payment.toCountry}
                    </TableCell>
                    <TableCell>
                      <Badge variant={
                        payment.status === 'completed' ? 'default' :
                        payment.status === 'processing' ? 'secondary' :
                        payment.status === 'pending' ? 'outline' : 'destructive'
                      }>
                        {payment.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(payment.timestamp).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Download className="mr-2 h-4 w-4" />
                            Download Receipt
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <Edit className="mr-2 h-4 w-4" />
                            Edit Payment
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Compliance Alerts */}
        <Card>
          <CardHeader>
            <CardTitle>Compliance Alerts</CardTitle>
            <CardDescription>Security and compliance notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Assigned To</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {complianceAlerts.map((alert) => (
                  <TableRow key={alert.id}>
                    <TableCell>
                      <Badge variant="outline" className="capitalize">
                        {alert.type}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{alert.description}</TableCell>
                    <TableCell>
                      <Badge variant={
                        alert.severity === 'high' || alert.severity === 'critical' ? 'destructive' :
                        alert.severity === 'medium' ? 'secondary' : 'outline'
                      }>
                        {alert.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{alert.assignedTo}</TableCell>
                    <TableCell>
                      <Badge variant={
                        alert.status === 'resolved' ? 'default' :
                        alert.status === 'investigating' ? 'secondary' : 'destructive'
                      }>
                        {alert.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem>
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem>
                            <Edit className="mr-2 h-4 w-4" />
                            Assign
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem>
                            <CheckCircle className="mr-2 h-4 w-4" />
                            Resolve
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );

  const renderPayments = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Payments</h1>
          <p className="text-muted-foreground">Manage cross-border payment transactions</p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Payment
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create New Payment</DialogTitle>
              <DialogDescription>
                Create a new cross-border payment transaction.
              </DialogDescription>
            </DialogHeader>
            <CreatePaymentForm onSubmit={createPayment} loading={loading} />
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Payment Transactions</CardTitle>
          <CardDescription>All payment transactions across the platform</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={payments}
            columns={[
              {
                key: 'id',
                label: 'Payment ID',
                sortable: true,
              },
              {
                key: 'recipientName',
                label: 'Recipient',
                sortable: true,
              },
              {
                key: 'amount',
                label: 'Amount',
                sortable: true,
                render: (value, payment) => `${payment.amount.toLocaleString()} ${payment.currency}`,
              },
              {
                key: 'fromCountry',
                label: 'Route',
                sortable: true,
                render: (value, payment) => `${payment.fromCountry} → ${payment.toCountry}`,
              },
              {
                key: 'status',
                label: 'Status',
                sortable: true,
                render: (value, payment) => (
                  <Badge variant={
                    payment.status === 'completed' ? 'default' :
                    payment.status === 'processing' ? 'secondary' :
                    payment.status === 'pending' ? 'outline' : 'destructive'
                  }>
                    {payment.status}
                  </Badge>
                ),
              },
              {
                key: 'timestamp',
                label: 'Date',
                sortable: true,
                render: (value, payment) => new Date(payment.timestamp).toLocaleDateString(),
              },
            ]}
            title="Payment Transactions"
            description="All payment transactions across the platform"
            searchable={true}
            filterable={true}
            exportable={true}
            pagination={true}
            pageSize={10}
          />
        </CardContent>
      </Card>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="text-muted-foreground">Manage platform users and permissions</p>
            </div>
        <Dialog open={showCreateUserModal} onOpenChange={setShowCreateUserModal}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new user to the platform with appropriate permissions.
              </DialogDescription>
            </DialogHeader>
            <CreateUserForm onSubmit={createUser} loading={loading} />
          </DialogContent>
        </Dialog>
            </div>

      <Card>
        <CardHeader>
          <CardTitle>User Management</CardTitle>
          <CardDescription>Platform users and their access levels</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Country</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-primary-foreground font-semibold text-xs">
                          {user.name.split(' ').map(n => n[0]).join('')}
                        </span>
        </div>
                      <span className="font-medium">{user.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={
                      user.role === 'admin' ? 'default' :
                      user.role === 'operator' ? 'secondary' : 'outline'
                    }>
                      {user.role}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={
                      user.status === 'active' ? 'default' :
                      user.status === 'inactive' ? 'secondary' : 'destructive'
                    }>
                      {user.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(user.lastLogin).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{user.country}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Eye className="mr-2 h-4 w-4" />
                          View Profile
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Edit className="mr-2 h-4 w-4" />
                          Edit User
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem>
                          <UserCheck className="mr-2 h-4 w-4" />
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuItem>
                          <Trash2 className="mr-2 h-4 w-4" />
                          Suspend User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      </div>
    );

  const renderContent = () => {
    switch (activeSection) {
      case 'dashboard':
        return renderDashboard();
      case 'payments':
        return renderPayments();
      case 'users':
        return renderUsers();
      case 'compliance':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold">Compliance Management</h1>
                <p className="text-muted-foreground">Monitor security alerts and regulatory compliance</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  Export Report
                </Button>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Alert
                </Button>
              </div>
            </div>

            {/* Compliance Metrics */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Alerts</CardTitle>
                  <AlertTriangle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{complianceAlerts.filter(a => a.status === 'open').length}</div>
                  <p className="text-xs text-muted-foreground">Requires attention</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Resolved</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{complianceAlerts.filter(a => a.status === 'resolved').length}</div>
                  <p className="text-xs text-muted-foreground">This month</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Critical</CardTitle>
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{complianceAlerts.filter(a => a.severity === 'critical').length}</div>
                  <p className="text-xs text-muted-foreground">High priority</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Compliance Score</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">98%</div>
                  <p className="text-xs text-muted-foreground">Excellent</p>
                </CardContent>
              </Card>
            </div>

            {/* Compliance Alerts Table */}
            <Card>
              <CardHeader>
                <CardTitle>Compliance Alerts ({complianceAlerts.length})</CardTitle>
                <CardDescription>Security and regulatory compliance alerts</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Timestamp</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {complianceAlerts.map((alert) => (
                        <TableRow key={alert.id}>
                          <TableCell className="font-medium">{alert.type}</TableCell>
                          <TableCell>
                            <Badge variant={
                              alert.severity === 'critical' ? 'destructive' :
                              alert.severity === 'high' ? 'default' :
                              alert.severity === 'medium' ? 'secondary' : 'outline'
                            }>
                              {alert.severity}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              alert.status === 'open' ? 'destructive' :
                              alert.status === 'resolved' ? 'default' : 'secondary'
                            }>
                              {alert.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{alert.description}</TableCell>
                          <TableCell className="text-muted-foreground">
                            {new Date(alert.timestamp).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Update Status
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Mark Resolved
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Compliance Reports */}
            <Card>
              <CardHeader>
                <CardTitle>Compliance Reports</CardTitle>
                <CardDescription>Generate and download compliance reports</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <h4 className="font-medium">AML Report</h4>
                    <p className="text-sm text-muted-foreground">Anti-Money Laundering compliance report</p>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-medium">KYC Report</h4>
                    <p className="text-sm text-muted-foreground">Know Your Customer verification report</p>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      case 'analytics':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-semibold">Analytics</h1>
              <p className="text-muted-foreground">Platform performance and insights</p>
        </div>
        
            {/* Analytics Metrics */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Volume</CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    ${analyticsData?.metrics?.totalVolume?.toLocaleString() || '0'}
            </div>
                  <p className="text-xs text-muted-foreground">
                    All-time transaction volume
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {analyticsData?.metrics?.successRate?.toFixed(1) || '0'}%
            </div>
                  <p className="text-xs text-muted-foreground">
                    Transaction success rate
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Transaction Time</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {analyticsData?.metrics?.avgTransactionTime || '0s'}
            </div>
                  <p className="text-xs text-muted-foreground">
                    Average processing time
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Users</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {analyticsData?.metrics?.activeUsers || '0'}
        </div>
                  <p className="text-xs text-muted-foreground">
                    Currently active users
                  </p>
                </CardContent>
              </Card>
      </div>
                
            {/* Payment Analytics */}
            <Card>
              <CardHeader>
                <CardTitle>Payment Analytics</CardTitle>
                <CardDescription>Detailed payment statistics and trends</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-primary">
                        {payments.length}
                      </div>
                      <div className="text-sm text-muted-foreground">Total Payments</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-green-600">
                        {payments.filter(p => p.status === 'completed').length}
                      </div>
                      <div className="text-sm text-muted-foreground">Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-3xl font-bold text-yellow-600">
                        {payments.filter(p => p.status === 'pending').length}
                      </div>
                      <div className="text-sm text-muted-foreground">Pending</div>
                    </div>
                </div>
                
                  <div className="grid gap-4 md:grid-cols-2">
                    <div>
                      <h4 className="font-medium mb-2">Currency Distribution</h4>
                      <div className="space-y-2">
                        {['USD', 'EUR', 'IRR', 'CNY'].map(currency => {
                          const count = payments.filter(p => p.currency === currency).length;
                          const percentage = payments.length > 0 ? (count / payments.length) * 100 : 0;
  return (
                            <div key={currency} className="flex items-center justify-between">
                              <span className="text-sm">{currency}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-primary h-2 rounded-full" 
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <span className="text-sm text-muted-foreground w-12 text-right">
                                  {count}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                </div>
                
          <div>
                      <h4 className="font-medium mb-2">Status Distribution</h4>
                      <div className="space-y-2">
                        {['completed', 'pending', 'processing', 'failed'].map(status => {
                          const count = payments.filter(p => p.status === status).length;
                          const percentage = payments.length > 0 ? (count / payments.length) * 100 : 0;
                          const color = status === 'completed' ? 'bg-green-500' : 
                                      status === 'pending' ? 'bg-yellow-500' :
                                      status === 'processing' ? 'bg-blue-500' : 'bg-red-500';
                          return (
                            <div key={status} className="flex items-center justify-between">
                              <span className="text-sm capitalize">{status}</span>
                              <div className="flex items-center gap-2">
                                <div className="w-20 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className={`${color} h-2 rounded-full`}
                                    style={{ width: `${percentage}%` }}
                                  ></div>
          </div>
                                <span className="text-sm text-muted-foreground w-12 text-right">
                                  {count}
                </span>
              </div>
            </div>
                          );
                        })}
          </div>
        </div>
                  </div>
                </div>
              </CardContent>
            </Card>

              {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>Latest platform activities and events</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {payments.slice(0, 5).map((payment) => (
                    <div key={payment.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${
                          payment.status === 'completed' ? 'bg-green-500' :
                          payment.status === 'pending' ? 'bg-yellow-500' :
                          payment.status === 'processing' ? 'bg-blue-500' : 'bg-red-500'
                        }`}></div>
                        <div>
                          <div className="font-medium">
                            Payment {payment.id} - {payment.amount} {payment.currency}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {payment.fromCountry} → {payment.toCountry}
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {new Date(payment.timestamp).toLocaleDateString()}
                      </div>
                      </div>
                    ))}
            </div>
              </CardContent>
            </Card>
            </div>
        );
      case 'monitoring':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-semibold">Monitoring</h1>
              <p className="text-muted-foreground">System health and performance monitoring</p>
            </div>
            
            {/* System Health */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">API Status</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">Healthy</div>
                  <p className="text-xs text-muted-foreground">
                    All endpoints operational
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Database</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">Connected</div>
                  <p className="text-xs text-muted-foreground">
                    KV store accessible
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Response Time</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {analyticsData?.metrics?.avgTransactionTime || '45ms'}
                </div>
                  <p className="text-xs text-muted-foreground">
                    Average response time
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Uptime</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">99.9%</div>
                  <p className="text-xs text-muted-foreground">
                    System availability
                  </p>
                </CardContent>
              </Card>
                </div>
                
            {/* Performance Metrics */}
            <Card>
              <CardHeader>
                <CardTitle>Performance Metrics</CardTitle>
                <CardDescription>Real-time system performance indicators</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <h4 className="font-medium mb-3">Transaction Processing</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Success Rate</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-green-500 h-2 rounded-full" 
                              style={{ width: `${analyticsData?.metrics?.successRate || 98.5}%` }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium">
                            {analyticsData?.metrics?.successRate?.toFixed(1) || '98.5'}%
                          </span>
                        </div>
                </div>
                
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Processing Time</span>
                        <span className="text-sm font-medium">
                          {analyticsData?.metrics?.avgTransactionTime || '45ms'}
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Throughput</span>
                        <span className="text-sm font-medium">10K TPS</span>
                      </div>
                </div>
              </div>

                        <div>
                    <h4 className="font-medium mb-3">System Resources</h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm">CPU Usage</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-blue-500 h-2 rounded-full" 
                              style={{ width: '45%' }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium">45%</span>
                          </div>
                        </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Memory Usage</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div 
                              className="bg-yellow-500 h-2 rounded-full" 
                              style={{ width: '62%' }}
                            ></div>
                          </div>
                          <span className="text-sm font-medium">62%</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm">Active Connections</span>
                        <span className="text-sm font-medium">
                          {analyticsData?.metrics?.activeUsers || '156'}
                        </span>
                      </div>
                  </div>
                </div>
                </div>
              </CardContent>
            </Card>

            {/* System Logs */}
            <Card>
              <CardHeader>
                <CardTitle>System Logs</CardTitle>
                <CardDescription>Recent system events and activities</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="bg-black text-green-400 p-4 rounded-md font-mono text-sm max-h-60 overflow-y-auto">
                  <div>[{new Date().toISOString()}] INFO: System monitoring active</div>
                  <div>[{new Date(Date.now() - 60000).toISOString()}] INFO: Payment processed successfully</div>
                  <div>[{new Date(Date.now() - 120000).toISOString()}] INFO: User authentication completed</div>
                  <div>[{new Date(Date.now() - 180000).toISOString()}] INFO: Compliance check passed</div>
                  <div>[{new Date(Date.now() - 240000).toISOString()}] INFO: Database connection stable</div>
                  <div>[{new Date(Date.now() - 300000).toISOString()}] INFO: API endpoints responding normally</div>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      case 'blockchain':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold">Blockchain Management</h1>
                <p className="text-muted-foreground">Manage blockchain networks and smart contracts</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh Networks
                </Button>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Deploy Contract
                </Button>
              </div>
            </div>

            {/* Blockchain Metrics */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Networks</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{blockchainNetworks.filter(n => n.status === 'connected').length}</div>
                  <p className="text-xs text-muted-foreground">Connected</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Smart Contracts</CardTitle>
                  <Shield className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{blockchainContracts.length}</div>
                  <p className="text-xs text-muted-foreground">Deployed</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Nodes</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {blockchainNetworks.reduce((sum, n) => sum + (n.nodes || 0), 0)}
                  </div>
                  <p className="text-xs text-muted-foreground">Across networks</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Network Health</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">98%</div>
                  <p className="text-xs text-muted-foreground">Excellent</p>
                </CardContent>
              </Card>
            </div>

            {/* Blockchain Networks */}
            <Card>
              <CardHeader>
                <CardTitle>Blockchain Networks ({blockchainNetworks.length})</CardTitle>
                <CardDescription>Manage and monitor blockchain network connections</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(2)].map((_, i) => (
                      <Skeleton key={i} className="h-16 w-full" />
                    ))}
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Network</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Nodes</TableHead>
                        <TableHead>Latency</TableHead>
                        <TableHead>Throughput</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blockchainNetworks.map((network) => (
                        <TableRow key={network.id}>
                          <TableCell className="font-medium">{network.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{network.type}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={
                              network.status === 'connected' ? 'default' :
                              network.status === 'planned' ? 'secondary' : 'destructive'
                            }>
                              {network.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{network.nodes}</TableCell>
                          <TableCell>{network.latency}ms</TableCell>
                          <TableCell>{network.throughput} TPS</TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem>
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem>
                                  <Activity className="h-4 w-4 mr-2" />
                                  Check Status
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem>
                                  <Settings className="h-4 w-4 mr-2" />
                                  Configure
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {/* Smart Contracts */}
            <Card>
              <CardHeader>
                <CardTitle>Smart Contracts ({blockchainContracts.length})</CardTitle>
                <CardDescription>Deployed smart contracts and their functions</CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-4">
                    {[...Array(2)].map((_, i) => (
                      <Skeleton key={i} className="h-20 w-full" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {blockchainContracts.map((contract) => (
                      <div key={contract.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <h4 className="font-medium">{contract.name}</h4>
                            <p className="text-sm text-muted-foreground">Version {contract.version}</p>
                          </div>
                          <Badge variant={contract.status === 'deployed' ? 'default' : 'secondary'}>
                            {contract.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Address:</span>
                            <span className="ml-2 font-mono">{contract.address}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Network:</span>
                            <span className="ml-2">{contract.network}</span>
                          </div>
                        </div>
                        <div className="mt-2">
                          <span className="text-sm text-muted-foreground">Functions:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {contract.functions?.map((func: string) => (
                              <Badge key={func} variant="outline" className="text-xs">
                                {func}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Blockchain Analytics */}
            {blockchainAnalytics && (
              <Card>
                <CardHeader>
                  <CardTitle>Blockchain Analytics</CardTitle>
                  <CardDescription>Network performance and transaction metrics</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="text-center">
                      <div className="text-2xl font-bold">{blockchainAnalytics.totalTransactions || 0}</div>
                      <p className="text-sm text-muted-foreground">Total Transactions</p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{blockchainAnalytics.averageLatency || 0}ms</div>
                      <p className="text-sm text-muted-foreground">Average Latency</p>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold">{blockchainAnalytics.successRate || 0}%</div>
                      <p className="text-sm text-muted-foreground">Success Rate</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        );
      case 'testimonials-demo':
        return <TestimonialsDemo />;
      case 'settings':
        return (
          <div className="space-y-6">
            <div>
              <h1 className="text-2xl font-semibold">Platform Settings</h1>
              <p className="text-muted-foreground">Configure platform settings and preferences</p>
            </div>
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>Basic platform configuration</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium">Platform Name</label>
                    <input
                      type="text"
                      defaultValue="NeoBridge"
                      className="w-full p-2 border rounded-md"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Default Currency</label>
                    <Select defaultValue="USD">
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select currency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                        <SelectItem value="IRR">IRR</SelectItem>
                        <SelectItem value="CNY">CNY</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium">Platform Description</label>
                  <textarea
                    defaultValue="Cross-border payment platform"
                    className="w-full p-2 border rounded-md"
                    rows={3}
                  />
                </div>
                <Button>Save General Settings</Button>
              </CardContent>
            </Card>

            {/* Security Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Security Settings</CardTitle>
                <CardDescription>Configure security policies and authentication</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Two-Factor Authentication</h4>
                      <p className="text-sm text-muted-foreground">Require 2FA for all users</p>
                    </div>
                    <Switch defaultChecked={false} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Session Timeout</h4>
                      <p className="text-sm text-muted-foreground">Auto-logout after inactivity</p>
                    </div>
                    <Select defaultValue="30">
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="15">15 minutes</SelectItem>
                        <SelectItem value="30">30 minutes</SelectItem>
                        <SelectItem value="60">1 hour</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Password Policy</h4>
                      <p className="text-sm text-muted-foreground">Minimum password requirements</p>
                    </div>
                    <Switch defaultChecked={true} />
                  </div>
                </div>
                <Button>Save Security Settings</Button>
              </CardContent>
            </Card>

            {/* Two-Factor Authentication */}
            <TwoFactorAuth 
              onSetupComplete={(method) => {
                setMessage({ text: `${method.toUpperCase()} authentication enabled successfully!`, type: 'success' });
              }}
              onDisable={(method) => {
                setMessage({ text: `${method.toUpperCase()} authentication disabled successfully!`, type: 'success' });
              }}
              user={{
                id: 'admin-user',
                email: 'admin@neobridge.com',
                phone: '+1234567890'
              }}
            />

            {/* Notification Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Notification Settings</CardTitle>
                <CardDescription>Configure notification preferences</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Email Notifications</h4>
                      <p className="text-sm text-muted-foreground">Send email alerts for important events</p>
                    </div>
                    <Switch defaultChecked={true} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">SMS Notifications</h4>
                      <p className="text-sm text-muted-foreground">Send SMS for critical alerts</p>
                    </div>
                    <Switch defaultChecked={false} />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Push Notifications</h4>
                      <p className="text-sm text-muted-foreground">Browser push notifications</p>
                    </div>
                    <Switch defaultChecked={true} />
                  </div>
                </div>
                <Button>Save Notification Settings</Button>
              </CardContent>
            </Card>

            {/* API Settings */}
            <Card>
              <CardHeader>
                <CardTitle>API Settings</CardTitle>
                <CardDescription>Configure API access and rate limiting</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium">API Rate Limit</label>
                    <Select defaultValue="500">
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select rate limit" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="100">100 requests/minute</SelectItem>
                        <SelectItem value="500">500 requests/minute</SelectItem>
                        <SelectItem value="1000">1000 requests/minute</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">API Version</label>
                    <Select defaultValue="v1">
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select version" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="v1">v1</SelectItem>
                        <SelectItem value="v2">v2 (Beta)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium">API Documentation</h4>
                    <p className="text-sm text-muted-foreground">Enable public API documentation</p>
                  </div>
                  <Button variant="outline" size="sm">View Docs</Button>
                </div>
                <Button>Save API Settings</Button>
              </CardContent>
            </Card>

            {/* System Information */}
            <Card>
              <CardHeader>
                <CardTitle>System Information</CardTitle>
                <CardDescription>Platform version and system details</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <span className="text-sm text-muted-foreground">Platform Version:</span>
                    <span className="ml-2 font-medium">v2.1.0</span>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Last Updated:</span>
                    <span className="ml-2 font-medium">2024-12-19</span>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Environment:</span>
                    <span className="ml-2 font-medium">Production</span>
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Database:</span>
                    <span className="ml-2 font-medium">Cloudflare D1</span>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Check for Updates
                  </Button>
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export Logs
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      default:
        return renderDashboard();
    }
  };

  return (
    <Layout
      currentPage={activeSection}
      onPageChange={setActiveSection}
      onRefresh={loadDashboardData}
      notifications={complianceAlerts.filter(alert => alert.status === 'open').length}
      user={{
        name: 'Admin User',
        email: 'admin@neobridge.com',
        role: 'Administrator'
      }}
    >
      {renderContent()}
      
      {/* Message Display */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-md shadow-lg ${
          message.type === 'success' ? 'bg-green-500 text-white' :
          message.type === 'error' ? 'bg-red-500 text-white' :
          'bg-blue-500 text-white'
        }`}>
          {message.text}
        </div>
      )}
    </Layout>
  );
};

export default App;